import React, { useState } from 'react';
import { fomaAll } from '../../fsm/rules/fomaAll';
import { pieRules } from '../../ruleSets/pieRules';
import { speakPhonemes } from '../../speak/speakPhonemes';
import { EditSpeakOptions } from '../EditSpeakOptions';
import { ChoiceList } from '../widgets/ChoiceList';
import { RadioList } from '../widgets/RadioList';
import { cleanOrLookupPie, convertPieToIpa, runPieRules } from './runPieRules';
import { loadPIEDictionary } from '../../loader/loadPIEDictionary';

/** UI for running rules against Proto-Indo-European */
export function PieArea() {
  // cascading text boxes + errors
  const [labelRaw, setLabelRaw] = useState('English input');
  const [rawInput, setRawInput] = useState('');
  const [errorRaw, setErrorRaw] = useState('');
  const [labelCleaned, setLabelCleaned] = useState('translated PIE');
  const [cleaned, setCleaned] = useState('');
  const [errorCleaned, setErrorCleaned] = useState('');
  const [afterRules, setAfterRules] = useState('');
  const [errorAfter, setErrorAfter] = useState('');
  const [finalIpa, setFinalIpa] = useState('');
  // rules
  const [inputType, setInputType] = useState(inputOptions[0]);
  const [ruleNames, setRuleNames] = useState(() => getRuleNames(getRuleType(inputType)));
  const [rulesName, setRulesName] = useState(ruleNames[0]);
  const [samples, setSamples] = useState(() => getSamples(inputOptions[0]));
  const [currentSample, setCurrentSample] = useState('');

  loadPIEDictionary();

  /** change what's in the 'raw' box, which impacts the 'cleaned' box */
  function changeRawInput(newRaw: string) {
    setRawInput(newRaw);
    const option = inputType.includes('dictionary') ? 'lookup'
      : inputType.includes('Laryngeal') ? 'cleanLaryngeal' : 'cleanSystem';
    const { result, error } = cleanOrLookupPie(newRaw, option);
    changeCleaned(result);
    setErrorRaw(error);
  }

  /** change what's in the 'cleaned' box, which impacts the 'after rules' box */
  function changeCleaned(newCleaned: string) {
    setCleaned(newCleaned);
    rerunRules(newCleaned)
  }
  function rerunRules(newCleaned?: string, newRulesName?: string) {
    const input = newCleaned ?? cleaned;
    const name = newRulesName ?? rulesName;
    const { result, error } = name === noRule ? { result: input, error: '' }
      : runPieRules(input, getRuleType(inputType), name);
    changeAfterRules(result);
    setErrorCleaned(error);
  }

  /** change what's in the 'after rules' text box, which impacts the 'final ipa' box */
  function changeAfterRules(newAfterRules: string) {
    setAfterRules(newAfterRules);
    const { result, error } = convertPieToIpa(newAfterRules);
    setFinalIpa(result);
    setErrorAfter(error);
  }

  /** change type of input: PIE dictionary, laryngeal PIE, & system PIE */
  function changeInputType(type: string) {
    setInputType(type);
    const currentNames = getRuleNames(getRuleType(type))
    setRuleNames(currentNames);
    setRulesName(currentNames[0]);
    setLabelRaw(type.includes('dictionary') ? 'English input' : 'PIE input');
    setLabelCleaned(type.includes('dictionary') ? 'translated PIE' : 'cleaned PIE');
    setSamples(getSamples(type));
    rerunRules();
  }
  /** change the specific set of rules */
  function changeRulesName(name: string) {
    setRulesName(name);
    rerunRules(undefined, name);
  }

  function changeSample(sample: string) {
    setCurrentSample(sample);
    changeRawInput(sample);
  }

  return (<>
    <h1>PIE (Proto-Indo-European)</h1>

    <div style={{ display: 'grid', gridTemplateColumns: 'auto 30%' }}>
      <div>
        <ChoiceList items={samples} selectedItem={currentSample} onSelect={changeSample} />
        <br /><br />

        {labelRaw}<br />
        <textarea cols={80} rows={4} spellCheck={false} value={rawInput}
          onChange={(foo) => changeRawInput(foo.target.value)} />
        <br />{errorRaw === '' ? '' : 'unknown: ' + errorRaw}

        <br />{labelCleaned}<br />
        <textarea cols={80} rows={4} spellCheck={false} value={cleaned}
          onChange={(event) => changeCleaned(event.target.value)} />
        <br />{errorCleaned === '' ? '' : 'unknown: ' + errorCleaned}

        <br />PIE with rules applied<br />
        <textarea cols={80} rows={4} spellCheck={false} value={afterRules}
          onChange={(event) => changeAfterRules(event.target.value)} />
        <br />{errorAfter === '' ? '' : 'unknown: ' + errorAfter}

        <br />IPA<br />
        <textarea cols={80} rows={4} spellCheck={false} value={finalIpa}
          onChange={(event) => setFinalIpa(event.target.value)} />

        <br /><br />
        <button onClick={() => speakPhonemes(finalIpa)}>speak output</button>
      </div>
      <div style={{ textAlign: 'left' }}>
        Type of input:<br />
        <RadioList items={inputOptions} selectedItem={inputType} onSelect={changeInputType} />
        <br />
        Convert to:<br />
        <RadioList items={ruleNames} selectedItem={rulesName} onSelect={changeRulesName} />
      </div>
    </div>

    <hr />
    <EditSpeakOptions />
    <br /><br />
  </>);
}

const inputOptions = [
  'English (Late PIE dictionary)',
  'Laryngeal PIE',
  'System PIE',
];

const pieDictionarySentences = [
  "",
  "one, two, three, four, five, six, seven, eight, nine, ten",
]

const laryngealPieSentences = [
  "",
  // Schleicher's fable, Byrd version from https://en.wikipedia.org/wiki/Schleicher%27s_fable
  "h₂áu̯ei̯ h₁i̯osméi̯ h₂u̯l̥h₁náh₂ né h₁ést, só h₁éḱu̯oms derḱt.",
  "só gʷr̥hₓúm u̯óǵʰom u̯eǵʰed; só méǵh₂m̥ bʰórom; só dʰǵʰémonm̥ h₂ṓḱu bʰered. h₂óu̯is h₁ékʷoi̯bʰi̯os u̯eu̯ked:",
  'dʰǵʰémonm̥ spéḱi̯oh₂ h₁éḱu̯oms-kʷe h₂áǵeti, ḱḗr moi̯ agʰnutor',
  // a couple reconstructions from https://en.wikipedia.org/wiki/Proto-Indo-European_numerals
  "hoino, dwo, trei, kʷetwor, pénkʷe, sweḱs, septm̥, oḱtō, h₁newn̥, deḱm̥",
  "hoinos, du̯oh₁, trei̯es, kʷétu̯ōr, pénkʷe, u̯éks, séptm̥, h₃eḱteh₃, néun, déḱm̥t",
  // https://en.wikipedia.org/wiki/Indo-European_vocabulary
  "méh₂tēr, ph₂tér, bʰréh₂ter, swésor, dʰugh₂tér, suhnú", // kinship
  "egh₂, h₁me, h₁mene, tu, wei, yū, swe", // pronouns
];

const systemPieSentences = [
  "",
  "molskirēɑh, o·nēɑh-, pundɑɦi̯o-, poɑhlno-, hɑleistēɑh·n-",
];

/** based on chosen input type (e.g. PIE dictionary), provide sample sentences */
function getSamples(inputType: string): string[] {
  return inputType.includes('dictionary') ? pieDictionarySentences
    : inputType.includes('Laryngeal') ? laryngealPieSentences : systemPieSentences;
}

/** based on chosen input type (e.g. PIE dictionary), figure out the type of rules we can run */
function getRuleType(inputType: string): 'laryngeal' | 'system' {
  return inputType === 'System PIE' ? 'system' : 'laryngeal';
}

/** get the list of rule names for this type of PIE input */
function getRuleNames(type: 'laryngeal' | 'system'): string[] {
  if (type === 'laryngeal') {
    if (laryngealNames.length === 0) {
      laryngealNames.push(noRule);
      Object.keys(pieRules).forEach(name => laryngealNames.push(name));
    }
    return laryngealNames;
  } else {
    if (systemNames.length === 0) {
      systemNames.push(noRule);
      Object.keys(fomaAll).forEach(name => systemNames.push(name));
    }
    return systemNames;
  }
}
let laryngealNames: string[] = [];
let systemNames: string[] = [];
const noRule = '<none>';
